import { useState } from 'react';
import {
  Button,
  FormControl,
  FormLabel,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Text,
  useToast,
} from '@chakra-ui/react';
import { uploadBeneficiaryDocument } from 'api/beneficiaries';
import { TOAST_STATUS_SUCCESS } from 'constants/constants';
import { capitalize } from 'lodash';
import {
  BENEFICIARY_DOCUMENT_ACCEPTED_FORMATS,
  BENEFICIARY_DOCUMENT_MAX_SIZE_BYTES,
} from 'modules/beneficiaries/constants/constants';
import { readFileAsBase64 } from 'modules/beneficiaries/utils/utils';
import { showError, showToast } from 'utils/notifications';

const UploadBeneficiaryDocumentModal = ({ isOpen, onClose, beneficiary, onUploaded }) => {
  const [fileToUpload, setFileToUpload] = useState(null);
  const [validationError, setValidationError] = useState('');
  const [isUploading, setIsUploading] = useState(false);

  const toast = useToast();

  const handleModalClose = () => {
    setFileToUpload(null);
    setValidationError('');
    setIsUploading(false);
    onClose();
  };

  const handleFileChange = (event) => {
    const file = event?.target?.files?.[0] ?? null;

    setValidationError('');

    if (file && file.size > BENEFICIARY_DOCUMENT_MAX_SIZE_BYTES) {
      setValidationError(`File size cannot exceed ${BENEFICIARY_DOCUMENT_MAX_SIZE_BYTES / (1024 * 1024)}MB`);
      setFileToUpload(null);

      return;
    }

    setFileToUpload(file);
  };

  const handleUpload = async () => {
    if (!fileToUpload) {
      setValidationError('Please select a document to upload');

      return;
    }

    setIsUploading(true);
    try {
      const documentBase64 = await readFileAsBase64(fileToUpload);

      await uploadBeneficiaryDocument(beneficiary?.id, {
        document_base64: documentBase64,
        document_name: fileToUpload?.name,
      });

      showToast(toast, capitalize(TOAST_STATUS_SUCCESS), 'Document uploaded successfully', TOAST_STATUS_SUCCESS);
      onUploaded?.();
      handleModalClose();
    } catch (error) {
      showError(toast, 'Failed to upload document', error, 'Failed to upload document');
      setIsUploading(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={handleModalClose} isCentered>
      <ModalOverlay />
      <ModalContent borderRadius='lg'>
        <ModalHeader>Upload Document</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <FormControl isInvalid={!!validationError}>
            <FormLabel>Supporting Document (PNG / JPEG / PDF)</FormLabel>
            <Input
              type='file'
              accept={BENEFICIARY_DOCUMENT_ACCEPTED_FORMATS}
              onChange={handleFileChange}
              pt={1}
              border='none'
            />
            {!!validationError && (
              <Text color='red.500' fontSize='sm' mt={2}>
                {validationError}
              </Text>
            )}
            <Text color='gray.500' fontSize='sm' mt={2}>
              Uploading a document does not change the beneficiary status.
            </Text>
          </FormControl>
        </ModalBody>
        <ModalFooter>
          <Button variant='ghost' mr={3} onClick={handleModalClose}>
            Cancel
          </Button>
          <Button colorScheme='blue' isLoading={isUploading} onClick={handleUpload}>
            Upload
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default UploadBeneficiaryDocumentModal;
